import { PERIODS, type TransactionPeriod } from "@/lib/api"

/**
 * Период списка транзакций и его память (AMEND-60 §2.1).
 *
 * Приоритет: адрес (`?period=…`, с ним приходит и ссылка «назад» из карточки) →
 * `localStorage` → `1D`. Свой диапазон запоминается вместе с `from`/`to`.
 * Чужое или битое значение отбрасывается целиком и уступает следующему
 * источнику: недопустимый период ушёл бы в ручку и вернулся 422 — таблица
 * открылась бы ошибкой вместо строк.
 *
 * Здесь нет ни React, ни `window` — только разбор и сборка строк, чтобы правила
 * можно было прогнать отдельно от страницы. Чтение и запись хранилища — две
 * обёртки внизу.
 */

/** Ключ хранилища — рядом с `antifraud.sidebar.collapsed` оболочки. */
export const PERIOD_STORAGE_KEY = "antifraud.transactions.period"

/**
 * Первый заход — сутки. Живые строки двойника — свежие, за ними владелица и
 * приходит; самое широкое окно (`12M`) открывало ~566 тыс. строк и каждый раз
 * требовало переключаться руками. Пустые сутки экран показывает честно: «No
 * transactions in this period» и подсказка расширить окно.
 */
export const DEFAULT_PERIOD: TransactionPeriod = "1D"

export interface PeriodChoice {
  period: TransactionPeriod
  /** Границы своего диапазона, `YYYY-MM-DD` из `<input type="date">`; у окон — пусто. */
  from: string
  to: string
}

const DAY = /^\d{4}-\d{2}-\d{2}$/
const KNOWN = new Set<string>([...PERIODS, "custom"])

function asPeriod(value: unknown): TransactionPeriod | null {
  return typeof value === "string" && KNOWN.has(value) ? (value as TransactionPeriod) : null
}

function asDay(value: unknown): string {
  return typeof value === "string" && DAY.test(value) ? value : ""
}

/** Границы живут только у `custom`: у окон они свои, серверные. */
function choice(period: TransactionPeriod, from: unknown, to: unknown): PeriodChoice {
  if (period !== "custom") return { period, from: "", to: "" }
  return { period, from: asDay(from), to: asDay(to) }
}

export function periodFromParams(params: URLSearchParams): PeriodChoice | null {
  const period = asPeriod(params.get("period"))
  return period ? choice(period, params.get("from"), params.get("to")) : null
}

export function periodFromStorage(raw: string | null): PeriodChoice | null {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (typeof parsed !== "object" || parsed === null) return null
  const stored = parsed as Record<string, unknown>
  const period = asPeriod(stored.period)
  return period ? choice(period, stored.from, stored.to) : null
}

export function resolvePeriod(params: URLSearchParams, stored: string | null): PeriodChoice {
  return (
    periodFromParams(params) ??
    periodFromStorage(stored) ?? { period: DEFAULT_PERIOD, from: "", to: "" }
  )
}

/** Что кладётся в хранилище — то же, что уходит в адрес. */
export function serializePeriod(value: PeriodChoice): string {
  const { period, from, to } = choice(value.period, value.from, value.to)
  return JSON.stringify(period === "custom" ? { period, from, to } : { period })
}

/** Адрес списка: только период и границы своего диапазона. */
export function periodParams(value: PeriodChoice): URLSearchParams {
  const { period, from, to } = choice(value.period, value.from, value.to)
  const params = new URLSearchParams({ period })
  if (from) params.set("from", from)
  if (to) params.set("to", to)
  return params
}

// Хранилище может быть выключено (приватный режим, политика браузера) и тогда
// бросает. Период из-за этого не ломается — просто не запоминается.

export function readStoredPeriod(): string | null {
  try {
    return window.localStorage.getItem(PERIOD_STORAGE_KEY)
  } catch {
    return null
  }
}

export function storePeriod(value: PeriodChoice): void {
  try {
    window.localStorage.setItem(PERIOD_STORAGE_KEY, serializePeriod(value))
  } catch {
    // см. выше
  }
}
